import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Eye, EyeOff, ShieldCheck, History, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Super Admin / Accounts control panel for salary visibility to
 * Franchise Owners. Writes to `salary_visibility_settings` and audits
 * every change to `salary_visibility_audit`.
 */
export const FranchiseSalaryVisibilityPanel = ({ session, centers = [] }) => {
  const [mode, setMode] = useState('single_month');
  const [center, setCenter] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [applyAll, setApplyAll] = useState(false);
  const [singleMonth, setSingleMonth] = useState(new Date().toISOString().slice(0, 7));
  const [fromMonth, setFromMonth] = useState('');
  const [toMonth, setToMonth] = useState('');
  const [fy, setFy] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [note, setNote] = useState('');

  const [settings, setSettings] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    if (!session?.token) return;
    try {
      const res = await fetch(`${API}/api/salary-visibility/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token }),
      });
      const j = await res.json();
      setSettings(j.rows || []);
    } catch { /* silent */ }
  }, [session]);

  const loadAudit = useCallback(async () => {
    if (!session?.token) return;
    try {
      const res = await fetch(`${API}/api/salary-visibility/audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token }),
      });
      const j = await res.json();
      setAudit(j.rows || []);
    } catch { /* silent */ }
  }, [session]);

  useEffect(() => { loadList(); }, [loadList]);

  const submit = async () => {
    if (!applyAll && !center) {
      toast.error('Select a centre or enable "Apply to all centres"');
      return;
    }
    const body = {
      token: session.token,
      mode, enabled, apply_all_centers: applyAll, note,
    };
    if (!applyAll) body.center = center;
    if (mode === 'single_month') body.month = singleMonth;
    if (mode === 'month_range') { body.from_month = fromMonth; body.to_month = toMonth; }
    if (mode === 'fy') body.fy = fy;
    if (mode === 'custom') { body.from_date = fromDate; body.to_date = toDate; }

    setBusy(true);
    try {
      const res = await fetch(`${API}/api/salary-visibility/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!res.ok) {
        toast.error(j.detail || 'Failed to update visibility');
        return;
      }
      toast.success(`Visibility ${enabled ? 'enabled' : 'disabled'} for ${j.centers.length} centre${j.centers.length > 1 ? 's' : ''} (${j.from_month} → ${j.to_month})`);
      await loadList();
      if (showAudit) await loadAudit();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Card className="bg-white border-slate-200/60" data-testid="salvis-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-indigo-700" /> Franchise Salary Visibility
          <Badge variant="outline" className="ml-2 text-[10px]">Super Admin / Accounts</Badge>
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Control which months of finalised salary each Franchise Owner may view. Default is <strong>HIDDEN</strong>. All changes are audited.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Centre</Label>
            <Select value={center} onValueChange={setCenter} disabled={applyAll}>
              <SelectTrigger data-testid="salvis-centre">
                <SelectValue placeholder="Select centre" />
              </SelectTrigger>
              <SelectContent>
                {centers.map(c => (
                  <SelectItem key={c.code || c} value={c.code || c}>{c.code || c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={applyAll} onCheckedChange={setApplyAll} data-testid="salvis-apply-all" />
            <span className="text-xs text-slate-700">Apply to all franchise centres</span>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="salvis-enabled" />
            <span className="text-xs text-slate-700 flex items-center gap-1">
              {enabled ? <><Eye className="w-3 h-3 text-emerald-600" /> Visible</> : <><EyeOff className="w-3 h-3 text-slate-500" /> Hidden</>}
            </span>
          </div>
        </div>

        <div>
          <Label className="text-xs">Period Type</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {[
              { v: 'single_month', l: 'Single Month' },
              { v: 'month_range', l: 'Month Range' },
              { v: 'fy', l: 'Financial Year' },
              { v: 'custom', l: 'Custom Dates' },
            ].map(o => (
              <Button
                key={o.v}
                size="sm"
                variant={mode === o.v ? 'default' : 'outline'}
                onClick={() => setMode(o.v)}
                data-testid={`salvis-mode-${o.v}`}
                className={mode === o.v ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}
              >
                {o.l}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {mode === 'single_month' && (
            <div>
              <Label className="text-xs">Month</Label>
              <Input type="month" value={singleMonth} onChange={(e) => setSingleMonth(e.target.value)} data-testid="salvis-single-month" />
            </div>
          )}
          {mode === 'month_range' && (
            <>
              <div>
                <Label className="text-xs">From Month</Label>
                <Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} data-testid="salvis-from-month" />
              </div>
              <div>
                <Label className="text-xs">To Month</Label>
                <Input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} data-testid="salvis-to-month" />
              </div>
            </>
          )}
          {mode === 'fy' && (
            <div>
              <Label className="text-xs">Financial Year</Label>
              <Input value={fy} placeholder="e.g. 2026-27" onChange={(e) => setFy(e.target.value)} data-testid="salvis-fy" />
            </div>
          )}
          {mode === 'custom' && (
            <>
              <div>
                <Label className="text-xs">From Date</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="salvis-from-date" />
              </div>
              <div>
                <Label className="text-xs">To Date</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="salvis-to-date" />
              </div>
            </>
          )}
          <div className="md:col-span-2">
            <Label className="text-xs">Note (optional — recorded in audit log)</Label>
            <Input value={note} placeholder="Reason / context" onChange={(e) => setNote(e.target.value)} data-testid="salvis-note" />
          </div>
        </div>

        <div className="flex items-center justify-between border-t pt-3">
          <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="salvis-save">
            {busy ? 'Saving…' : 'Save Visibility'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              setShowAudit(v => !v);
              if (!showAudit) await loadAudit();
            }}
            data-testid="salvis-audit-toggle"
          >
            <History className="w-4 h-4 mr-1" /> {showAudit ? 'Hide' : 'View'} Audit Log
          </Button>
        </div>

        {/* Current settings table */}
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1">Current Settings</p>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="text-xs">Centre</TableHead>
                  <TableHead className="text-xs">Window</TableHead>
                  <TableHead className="text-xs">Mode</TableHead>
                  <TableHead className="text-xs">Visibility</TableHead>
                  <TableHead className="text-xs">Updated By</TableHead>
                  <TableHead className="text-xs">Updated At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {settings.map((s, i) => (
                  <TableRow key={i} data-testid={`salvis-row-${s.center}`}>
                    <TableCell className="text-xs font-medium">{s.center}</TableCell>
                    <TableCell className="text-xs">{s.from_month} → {s.to_month}</TableCell>
                    <TableCell className="text-xs">{s.mode}</TableCell>
                    <TableCell className="text-xs">
                      {s.enabled
                        ? <Badge className="bg-emerald-100 text-emerald-700">Visible</Badge>
                        : <Badge className="bg-slate-100 text-slate-600">Hidden</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">{s.updated_by} ({s.updated_by_role})</TableCell>
                    <TableCell className="text-xs">{String(s.updated_at).slice(0, 19).replace('T', ' ')}</TableCell>
                  </TableRow>
                ))}
                {settings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-6 text-slate-400 text-xs">
                      No visibility settings yet — all centres are HIDDEN by default.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {showAudit && (
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1">Audit Log (latest 500)</p>
            <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-72 overflow-y-auto">
              <Table>
                <TableHeader className="bg-slate-50 sticky top-0">
                  <TableRow>
                    <TableHead className="text-xs">Timestamp</TableHead>
                    <TableHead className="text-xs">Centre</TableHead>
                    <TableHead className="text-xs">Window</TableHead>
                    <TableHead className="text-xs">Enabled</TableHead>
                    <TableHead className="text-xs">By</TableHead>
                    <TableHead className="text-xs">Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audit.map((r, i) => (
                    <TableRow key={i} data-testid={`salvis-audit-${i}`}>
                      <TableCell className="text-xs">{String(r.timestamp).slice(0, 19).replace('T', ' ')}</TableCell>
                      <TableCell className="text-xs">{r.center}</TableCell>
                      <TableCell className="text-xs">{r.from_month} → {r.to_month}</TableCell>
                      <TableCell className="text-xs">{r.enabled ? 'ON' : 'OFF'}</TableCell>
                      <TableCell className="text-xs">{r.changed_by} ({r.changed_by_role})</TableCell>
                      <TableCell className="text-xs">{r.note || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {audit.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4 text-slate-400 text-xs">
                        No audit entries yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
};

export default FranchiseSalaryVisibilityPanel;
